
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DateSection } from "@/components/client-form/DateSection";
import { BasicInfoSection } from "@/components/client-form/BasicInfoSection";
import { ServiceSection } from "@/components/client-form/ServiceSection";
import { CommunicationSection } from "@/components/client-form/CommunicationSection";
import { ClientFormData } from "@/types/client";
import { subscribeToMailchimp } from "@/utils/mailchimp";

const ClientInfo = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    clientName: "",
    industry: "",
    lastContactDate: "",
    meetingContext: "",
    serviceUsed: "",
    serviceOutcome: "",
    appreciation: "",
    emailTone: "Professional",
    desiredAction: "Reply to email"
  });

  useEffect(() => {
    const tempUserEmail = localStorage.getItem('tempUserEmail');
    if (!tempUserEmail) {
      toast({
        title: "Missing Information",
        description: "Please start from the homepage to enter your information.",
        variant: "destructive",
      });
      navigate("/", { replace: true });
    }
  }, [navigate, toast]);

  const handleFormDataChange = (newData: Partial<ClientFormData>) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      handleFormDataChange({ lastContactDate: date.toISOString().split('T')[0] });
    }
  };

  const generateMessage = async (clientId: string, messageType: 'email' | 'text', userName: string) => {
    const { data, error } = await supabase.functions.invoke('generate-message', {
      body: {
        ...formData,
        messageType,
        userName,
      },
    });

    if (error) throw error;
    return data.content;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      const tempUserEmail = localStorage.getItem('tempUserEmail');
      if (!tempUserEmail) {
        throw new Error('User information not found. Please start from the homepage.');
      }

      // Get temp user info
      const { data: tempUser, error: userError } = await supabase
        .from('temp_users')
        .select('*')
        .eq('email', tempUserEmail)
        .single();

      if (userError) throw userError;

      // Create the client record
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert([
          {
            temp_user_id: tempUser.id,
            client_name: formData.clientName,
            industry: formData.industry,
            meeting_context: formData.meetingContext,
            service_used: formData.serviceUsed,
            service_outcome: formData.serviceOutcome,
            appreciation: formData.appreciation,
            email_tone: formData.emailTone,
            desired_action: formData.desiredAction,
            last_contact_date: formData.lastContactDate
          }
        ])
        .select()
        .single();

      if (clientError) throw clientError;

      // Add user to Mailchimp with NonSubscribed tag
      try {
        await subscribeToMailchimp({
          email: tempUser.email,
          firstName: tempUser.name.split(' ')[0],
          lastName: tempUser.name.split(' ').length > 1 ? tempUser.name.split(' ').slice(1).join(' ') : undefined,
          tags: ["content-generator"],
          status: "NonSubscribed"
        });
      } catch (mailchimpError) {
        console.error("Failed to add user to Mailchimp:", mailchimpError);
        // Don't stop the process for mailchimp errors
      }

      // Generate both email and text messages using AI
      const messageTypes = ['email', 'text'] as const;
      const messages = await Promise.all(
        messageTypes.map(async (messageType) => {
          const content = await generateMessage(client.id, messageType, tempUser.name);
          
          const { error: insertError, data: messageData } = await supabase
            .from('generated_messages')
            .insert([{
              client_id: client.id,
              message_type: messageType,
              content
            }])
            .select()
            .single();

          if (insertError) throw insertError;
          return messageData;
        })
      );

      // Store messages in localStorage for non-authenticated users
      localStorage.setItem('generatedMessages', JSON.stringify(messages));
      
      toast({
        title: "Success!",
        description: "Your AI-generated messages are ready.",
      });

      navigate("/generated-content");
    } catch (error: any) {
      console.error('Error during submission:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Tell us about your client
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            This information will help us generate personalized messages
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <BasicInfoSection
              formData={formData}
              onFormDataChange={handleFormDataChange}
            />

            <DateSection
              date={formData.lastContactDate ? new Date(formData.lastContactDate) : undefined}
              onDateChange={handleDateChange}
            />

            <ServiceSection
              formData={formData}
              onFormDataChange={handleFormDataChange}
            />

            <CommunicationSection
              formData={formData}
              onFormDataChange={handleFormDataChange}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Generating Messages..." : "Generate Messages"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ClientInfo;
